// src/problem.ts
import { z } from "zod";
var problemFieldErrorSchema = z.object({
  path: z.string(),
  messageKey: z.string()
});
var problemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int().min(100).max(599),
  detail: z.string().optional(),
  instance: z.string().optional(),
  messageKey: z.string(),
  errors: z.array(problemFieldErrorSchema).optional()
});

// src/pagination.ts
import { z as z2 } from "zod";
var cursorQuerySchema = z2.object({
  cursor: z2.string().min(1).optional(),
  limit: z2.coerce.number().int().min(1).max(100).default(50)
});
function paginated(itemSchema) {
  return z2.object({
    items: z2.array(itemSchema),
    nextCursor: z2.string().nullable()
  });
}

// src/health.ts
import { z as z3 } from "zod";
var healthCheckStateSchema = z3.enum(["up", "down"]);
var healthStatusSchema = z3.object({
  status: z3.enum(["ok", "degraded"]),
  checks: z3.record(z3.string(), healthCheckStateSchema),
  version: z3.string()
});

// src/route.ts
function defineRoute(definition) {
  return definition;
}

// src/routes/health.ts
var healthRoutes = {
  live: defineRoute({
    method: "GET",
    path: "/v1/health/live",
    summary: "Liveness probe",
    auth: "public",
    response: healthStatusSchema
  }),
  ready: defineRoute({
    method: "GET",
    path: "/v1/health/ready",
    summary: "Readiness probe including Postgres and Redis",
    auth: "public",
    response: healthStatusSchema
  })
};

// src/openapi.ts
import { z as z4 } from "zod";
function toJsonSchema(schema, io = "output") {
  return z4.toJSONSchema(schema, { target: "draft-2020-12", io });
}
function parameters(location, schema) {
  if (!schema) return [];
  const json = toJsonSchema(schema, "input");
  const properties = json.properties ?? {};
  const required = new Set(json.required ?? []);
  return Object.entries(properties).map(([name, property]) => ({
    name,
    in: location,
    required: location === "path" ? true : required.has(name),
    schema: property
  }));
}
var toOpenApiPath = (path) => path.replace(/:([A-Za-z0-9_]+)(?=\/|$)/g, "{$1}");
var responses = (route) => {
  const status = route.successStatus ?? (route.response instanceof z4.ZodNull ? 204 : 200);
  if (status === 204) return { "204": { description: "No Content" } };
  return {
    [status]: {
      description: status === 202 ? "Accepted" : "Success",
      content: { "application/json": { schema: toJsonSchema(route.response) } }
    }
  };
};
function buildOpenApiDocument(routes, info) {
  const paths = {};
  for (const route of routes) {
    const method = route.method.toLowerCase();
    const operation = {
      summary: route.summary,
      parameters: [
        ...parameters("path", route.params) ?? [],
        ...parameters("query", route.query) ?? []
      ],
      responses: responses(route)
    };
    if (route.body) {
      operation.requestBody = {
        required: true,
        content: { "application/json": { schema: toJsonSchema(route.body, "input") } }
      };
    }
    if (route.auth !== "public") operation.security = [{ sessionCookie: [] }];
    const path = toOpenApiPath(route.path);
    paths[path] = { ...paths[path], [method]: operation };
  }
  return {
    openapi: "3.1.0",
    info,
    paths,
    components: {
      securitySchemes: { sessionCookie: { type: "apiKey", in: "cookie", name: "pickpro_access" } }
    }
  };
}

// src/common.ts
import { SPORTS } from "@pickpro/domain";
import { z as z5 } from "zod";
var isValidTimezone = (zone) => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};
var localeSchema = z5.enum(["en", "pt-BR", "es"]);
var timezoneSchema = z5.string().min(1).max(64).refine(isValidTimezone);
var sportSchema = z5.enum(SPORTS);
var planStatusSchema = z5.enum(["beta", "active", "inactive"]);
var oddsFormatSchema = z5.enum(["decimal", "fractional", "american"]);
var userRoleSchema = z5.enum(["user", "admin"]);
var acceptedSchema = z5.object({ accepted: z5.literal(true) });
var noContentSchema = z5.null();
var idParamsSchema = z5.object({ id: z5.uuid() });

// src/auth.ts
import { z as z6 } from "zod";
var emailSchema = z6.string().trim().toLowerCase().pipe(z6.email().max(254));
var passwordSchema = z6.string().min(10).max(128);
var tokenSchema = z6.string().min(32).max(256);
var registerBodySchema = z6.object({
  firstName: z6.string().trim().min(1).max(100),
  lastName: z6.string().trim().min(1).max(100),
  email: emailSchema,
  password: passwordSchema,
  locale: localeSchema.optional(),
  timezone: timezoneSchema.optional()
});
var loginBodySchema = z6.object({
  email: emailSchema,
  password: z6.string().min(1).max(128)
});
var tokenBodySchema = z6.object({ token: tokenSchema });
var emailBodySchema = z6.object({ email: emailSchema });
var resetPasswordBodySchema = z6.object({ token: tokenSchema, password: passwordSchema });
var favouriteCompetitionIdsSchema = z6.array(z6.uuid()).max(100);
var settingsSchema = z6.object({
  locale: localeSchema,
  timezone: timezoneSchema,
  defaultSport: sportSchema.nullable(),
  oddsFormat: oddsFormatSchema,
  favouriteCompetitionIds: favouriteCompetitionIdsSchema.optional()
});
var meSchema = z6.object({
  id: z6.uuid(),
  email: z6.email(),
  role: userRoleSchema,
  planStatus: planStatusSchema,
  sports: z6.array(sportSchema),
  settings: settingsSchema
});

// src/users.ts
import { z as z7 } from "zod";
var DELETE_CONFIRMATION = "DELETE";
var updateSettingsBodySchema = settingsSchema.partial().refine((value) => Object.keys(value).length > 0);
var changePasswordBodySchema = z7.object({
  currentPassword: z7.string().min(1).max(128),
  newPassword: passwordSchema
});
var deleteAccountBodySchema = z7.object({
  password: z7.string().min(1).max(128),
  confirmation: z7.literal(DELETE_CONFIRMATION)
});

// src/admin.ts
import { z as z8 } from "zod";
var adminUserSchema = z8.object({
  firstName: z8.string().nullable(),
  lastName: z8.string().nullable(),
  id: z8.uuid(),
  email: z8.email(),
  role: userRoleSchema,
  planStatus: planStatusSchema,
  sports: z8.array(sportSchema),
  emailVerifiedAt: z8.iso.datetime().nullable(),
  firstGrantedAt: z8.iso.datetime().nullable(),
  createdAt: z8.iso.datetime()
});
var adminUsersQuerySchema = cursorQuerySchema.extend({
  q: z8.string().trim().min(1).max(254).optional(),
  role: userRoleSchema.optional(),
  planStatus: planStatusSchema.optional(),
  verified: z8.enum(["true", "false"]).optional(),
  enabled: z8.enum(["true", "false"]).optional(),
  sport: sportSchema.optional()
});
var adminOverviewSchema = z8.object({
  totalUsers: z8.number().int().nonnegative(),
  verifiedUsers: z8.number().int().nonnegative(),
  adminUsers: z8.number().int().nonnegative(),
  usersWithAccess: z8.number().int().nonnegative(),
  planStatuses: z8.record(planStatusSchema, z8.number().int().nonnegative()),
  sportAccess: z8.record(sportSchema, z8.number().int().nonnegative())
});
var adminUserDetailsSchema = z8.object({
  user: adminUserSchema,
  grants: z8.array(
    z8.object({
      sport: sportSchema,
      grantedAt: z8.iso.datetime(),
      expiresAt: z8.iso.datetime().nullable(),
      grantedBy: z8.uuid().nullable()
    })
  ),
  activeSessions: z8.number().int().nonnegative()
});
var loginHistoryQuerySchema = z8.object({
  cursor: z8.uuid().optional(),
  limit: z8.coerce.number().int().min(1).max(100).default(25)
});
var loginHistoryEntrySchema = z8.object({
  id: z8.uuid(),
  loggedInAt: z8.iso.datetime(),
  ipAddress: z8.string().nullable(),
  country: z8.string().nullable(),
  region: z8.string().nullable(),
  city: z8.string().nullable(),
  userAgent: z8.string().nullable()
});
var INGESTION_FAMILIES = [
  "leagues",
  "seasons",
  "schedules",
  "teams",
  "squads",
  "standings",
  "fixture-batches",
  "odds",
  "live-feeds",
  "games",
  "stats",
  "box-scores",
  "other"
];
var ingestionFamilySchema = z8.enum(INGESTION_FAMILIES);
var adminIngestionQuerySchema = z8.object({ sport: sportSchema });
var count = () => z8.number().int().nonnegative();
var ingestionTaskFamilySchema = z8.object({
  family: ingestionFamilySchema,
  pending: count(),
  running: count(),
  complete: count(),
  failed: count(),
  retired: count(),
  restricted: count(),
  oldestPendingPriority: z8.number().int().nullable(),
  oldestPendingSince: z8.iso.datetime().nullable()
});
var ingestionFeedSchema = z8.object({
  endpoint: z8.string().min(1),
  lastCompletedAt: z8.iso.datetime().nullable(),
  cadenceMs: z8.number().int().positive().optional()
});
var ingestionStorageSchema = z8.object({
  records: count(),
  versions: count(),
  fixtures: count(),
  teamMetrics: count(),
  playerMetrics: count(),
  quotes: count(),
  approximate: z8.boolean()
});
var adminIngestionSchema = z8.object({
  provider: z8.object({
    name: z8.enum(["sportmonks", "balldontlie"]),
    status: z8.enum(["healthy", "issues", "stale", "unknown", "not_configured"]),
    failed: count(),
    restricted: count(),
    parked: count(),
    lastAttemptAt: z8.iso.datetime().nullable(),
    lastSuccessAt: z8.iso.datetime().nullable()
  }),
  tasks: z8.array(ingestionTaskFamilySchema),
  feeds: z8.array(ingestionFeedSchema),
  outbox: z8.object({ pending: count() }),
  storage: ingestionStorageSchema
});
var ingestionParticipantSchema = z8.object({
  side: z8.string().min(1),
  teamId: z8.uuid(),
  teamName: z8.string(),
  storedFinishedBefore: count(),
  withTeamMetrics: count(),
  withPlayerMetrics: count(),
  lastMetricFixtureDate: z8.iso.date().nullable()
});
var adminIngestionFixtureSchema = z8.object({
  fixture: z8.object({
    id: z8.uuid(),
    sport: sportSchema,
    date: z8.iso.date(),
    status: z8.string(),
    competition: z8.string().nullable(),
    providerId: z8.string()
  }),
  participants: z8.array(ingestionParticipantSchema),
  quotes: count(),
  pendingBatchesForTeams: count()
});
var updateAdminUserBodySchema = z8.object({ planStatus: planStatusSchema.optional(), sports: z8.array(sportSchema).optional() }).refine((value) => value.planStatus !== void 0 || value.sports !== void 0);

// src/routes/auth.ts
var authRoutes = {
  register: defineRoute({
    method: "POST",
    path: "/v1/auth/register",
    summary: "Create an account and send a verification email",
    auth: "public",
    body: registerBodySchema,
    response: acceptedSchema,
    successStatus: 202
  }),
  verifyEmail: defineRoute({
    method: "POST",
    path: "/v1/auth/verify-email",
    summary: "Consume a verification token",
    auth: "public",
    body: tokenBodySchema,
    response: acceptedSchema
  }),
  resendVerification: defineRoute({
    method: "POST",
    path: "/v1/auth/resend-verification",
    summary: "Send a fresh verification email",
    auth: "public",
    body: emailBodySchema,
    response: acceptedSchema,
    successStatus: 202
  }),
  login: defineRoute({
    method: "POST",
    path: "/v1/auth/login",
    summary: "Issue access and refresh cookies",
    auth: "public",
    body: loginBodySchema,
    response: meSchema
  }),
  refresh: defineRoute({
    method: "POST",
    path: "/v1/auth/refresh",
    summary: "Rotate the refresh token",
    auth: "public",
    response: acceptedSchema
  }),
  logout: defineRoute({
    method: "POST",
    path: "/v1/auth/logout",
    summary: "Revoke the current session family and clear cookies",
    auth: "public",
    response: noContentSchema
  }),
  forgotPassword: defineRoute({
    method: "POST",
    path: "/v1/auth/forgot-password",
    summary: "Send a password reset email",
    auth: "public",
    body: emailBodySchema,
    response: acceptedSchema,
    successStatus: 202
  }),
  resetPassword: defineRoute({
    method: "POST",
    path: "/v1/auth/reset-password",
    summary: "Consume a reset token and set a new password",
    auth: "public",
    body: resetPasswordBodySchema,
    response: acceptedSchema
  }),
  me: defineRoute({
    method: "GET",
    path: "/v1/auth/me",
    summary: "Current user, role, plan status, sports and settings",
    auth: "user",
    response: meSchema
  })
};

// src/routes/users.ts
var userRoutes = {
  getSettings: defineRoute({
    method: "GET",
    path: "/v1/users/settings",
    summary: "Read locale, timezone, default sport and odds format",
    auth: "user",
    response: settingsSchema
  }),
  updateSettings: defineRoute({
    method: "PATCH",
    path: "/v1/users/settings",
    summary: "Update settings",
    auth: "user",
    body: updateSettingsBodySchema,
    response: settingsSchema
  }),
  changePassword: defineRoute({
    method: "POST",
    path: "/v1/users/password",
    summary: "Change the password and revoke other sessions",
    auth: "user",
    body: changePasswordBodySchema,
    response: noContentSchema
  }),
  deleteAccount: defineRoute({
    method: "DELETE",
    path: "/v1/users/me",
    summary: "Delete the account",
    auth: "user",
    body: deleteAccountBodySchema,
    response: noContentSchema
  })
};

// src/routes/admin.ts
var adminRoutes = {
  loginHistory: defineRoute({
    method: "GET",
    path: "/v1/admin/users/:id/login-history",
    summary: "Successful user logins, newest first",
    auth: "admin",
    params: idParamsSchema,
    query: loginHistoryQuerySchema,
    response: paginated(loginHistoryEntrySchema)
  }),
  overview: defineRoute({
    method: "GET",
    path: "/v1/admin/overview",
    summary: "Account and effective access totals",
    auth: "admin",
    response: adminOverviewSchema
  }),
  userDetails: defineRoute({
    method: "GET",
    path: "/v1/admin/users/:id",
    summary: "Account details and stored grants",
    auth: "admin",
    params: idParamsSchema,
    response: adminUserDetailsSchema
  }),
  listUsers: defineRoute({
    method: "GET",
    path: "/v1/admin/users",
    summary: "Search users with cursor pagination",
    auth: "admin",
    query: adminUsersQuerySchema,
    response: paginated(adminUserSchema)
  }),
  ingestion: defineRoute({
    method: "GET",
    path: "/v1/admin/ingestion",
    summary: "Task, feed and storage counters for one sport",
    auth: "admin",
    query: adminIngestionQuerySchema,
    response: adminIngestionSchema
  }),
  ingestionFixture: defineRoute({
    method: "GET",
    path: "/v1/admin/ingestion/fixtures/:id",
    summary: "Stored research coverage behind one fixture",
    auth: "admin",
    params: idParamsSchema,
    response: adminIngestionFixtureSchema
  }),
  updateUser: defineRoute({
    method: "PATCH",
    path: "/v1/admin/users/:id",
    summary: "Set plan status and entitled sports",
    auth: "admin",
    params: idParamsSchema,
    body: updateAdminUserBodySchema,
    response: adminUserSchema
  })
};

// src/matches.ts
import { isPeriod, SAMPLE_SIZES } from "@pickpro/domain";
import { z as z10 } from "zod";

// src/football-context.ts
import { z as z9 } from "zod";
var side = z9.enum(["home", "away"]);
var footballContextSchema = z9.object({
  observedAt: z9.iso.datetime(),
  stale: z9.boolean(),
  neutralVenue: z9.boolean().nullable(),
  weather: z9.object({
    temperatureCelsius: z9.number(),
    humidity: z9.number().min(0).max(100).nullable(),
    kind: z9.enum(["actual", "forecast"])
  }).nullable(),
  teams: z9.array(
    z9.object({
      side,
      name: z9.string(),
      teamId: z9.string(),
      formation: z9.string().nullable(),
      coach: z9.string().nullable(),
      restHours: z9.number().nonnegative().nullable(),
      matchesLast7Days: z9.number().int().nonnegative(),
      lineupStatus: z9.enum(["confirmed", "expected", "unconfirmed", "unknown"]),
      players: z9.array(
        z9.object({
          name: z9.string(),
          number: z9.number().nullable(),
          role: z9.enum(["starting", "bench", "expected"])
        })
      ),
      absences: z9.array(
        z9.object({ name: z9.string(), reason: z9.enum(["injury", "suspension", "unavailable"]) })
      )
    })
  ).max(2),
  probabilities: z9.object({
    home: z9.number().min(0).max(1),
    draw: z9.number().min(0).max(1),
    away: z9.number().min(0).max(1)
  }).nullable(),
  liveProbabilities: z9.object({
    minute: z9.number().int().nonnegative(),
    home: z9.number().min(0).max(1),
    draw: z9.number().min(0).max(1),
    away: z9.number().min(0).max(1),
    observedAt: z9.iso.datetime()
  }).nullable(),
  expectedGoals: z9.array(z9.object({ side, value: z9.number().nonnegative() })),
  pressure: z9.array(
    z9.object({
      side,
      minute: z9.number().nonnegative(),
      period: z9.string(),
      value: z9.number().nonnegative()
    })
  ),
  trends: z9.array(
    z9.object({
      side,
      minute: z9.number().nonnegative(),
      period: z9.string(),
      value: z9.number().nonnegative(),
      metric: z9.enum([
        "possession",
        "attacks",
        "dangerous_attacks",
        "corners",
        "shots",
        "shots_on_target"
      ])
    })
  ).optional(),
  facts: z9.array(z9.string()),
  news: z9.array(
    z9.object({
      title: z9.string(),
      lines: z9.array(z9.string()),
      kind: z9.enum(["preview", "report"])
    })
  ),
  events: z9.array(
    z9.object({
      side: side.nullable(),
      minute: z9.number().nonnegative(),
      player: z9.string(),
      kind: z9.enum([
        "goal",
        "ownGoal",
        "penalty",
        "missedPenalty",
        "yellowCard",
        "redCard",
        "substitution",
        "var",
        "varCard"
      ])
    })
  )
});

// src/matches.ts
var matchesQuerySchema = z10.object({
  date: z10.iso.date(),
  timezone: timezoneSchema.default("UTC")
});
var matchParamsSchema = z10.object({ sport: sportSchema, id: z10.uuid() });
var matchStatusSchema = z10.enum([
  "scheduled",
  "live",
  "finished",
  "postponed",
  "cancelled",
  "suspended",
  "delayed",
  "abandoned",
  "awarded",
  "unknown"
]);
var formResultSchema = z10.enum(["W", "D", "L"]);
var teamFormSchema = z10.array(formResultSchema).max(5);
var matchPricesSchema = z10.object({
  bookmaker: z10.string(),
  capturedAt: z10.iso.datetime(),
  home: z10.number().nullable(),
  draw: z10.number().nullable(),
  away: z10.number().nullable()
});
var matchSchema = z10.object({
  id: z10.uuid(),
  sport: sportSchema,
  date: z10.iso.date(),
  startsAt: z10.iso.datetime().nullable(),
  competition: z10.string(),
  competitionId: z10.uuid().nullable(),
  competitionLogoUrl: z10.string().nullable().default(null),
  round: z10.string().nullable(),
  homeTeamId: z10.uuid(),
  awayTeamId: z10.uuid(),
  homeName: z10.string(),
  awayName: z10.string(),
  homeLogoUrl: z10.string().nullable().default(null),
  awayLogoUrl: z10.string().nullable().default(null),
  homeScore: z10.number().nullable(),
  awayScore: z10.number().nullable(),
  homeRank: z10.number().int().nullable().default(null),
  awayRank: z10.number().int().nullable().default(null),
  homeForm: teamFormSchema.default([]),
  awayForm: teamFormSchema.default([]),
  minute: z10.number().int().nullable().default(null),
  period: z10.string().nullable().default(null),
  prices: matchPricesSchema.nullable().default(null),
  status: matchStatusSchema,
  syncedAt: z10.iso.datetime()
});
var matchesResponseSchema = z10.object({
  items: z10.array(matchSchema),
  sync: z10.object({
    status: z10.enum(["ready", "updating", "unavailable"]),
    lastSuccessAt: z10.iso.datetime().nullable(),
    stale: z10.boolean()
  })
});
var matchParticipantSchema = z10.object({
  side: z10.enum(["home", "away"]),
  teamId: z10.uuid(),
  teamName: z10.string(),
  abbreviation: z10.string().nullable(),
  logoUrl: z10.string().nullable(),
  score: z10.number().nullable(),
  scoreDetail: z10.record(z10.string(), z10.number()).nullable(),
  isWinner: z10.boolean().nullable(),
  rank: z10.number().nullable(),
  form: teamFormSchema.default([])
});
var headToHeadMatchSchema = z10.object({
  id: z10.uuid(),
  date: z10.iso.date(),
  startsAt: z10.iso.datetime().nullable(),
  status: matchStatusSchema,
  homeTeamId: z10.uuid(),
  homeName: z10.string(),
  homeScore: z10.number().nullable(),
  awayTeamId: z10.uuid(),
  awayName: z10.string(),
  awayScore: z10.number().nullable()
});
var matchRefereeSchema = z10.object({
  id: z10.uuid(),
  name: z10.string(),
  cardsPerGame: z10.number().nullable(),
  sample: z10.number().int()
});
var matchDetailSchema = z10.object({
  context: footballContextSchema.nullable().optional(),
  id: z10.uuid(),
  sport: sportSchema,
  date: z10.iso.date(),
  startsAt: z10.iso.datetime().nullable(),
  status: matchStatusSchema,
  round: z10.string().nullable(),
  competition: z10.object({ id: z10.uuid(), name: z10.string() }).nullable(),
  season: z10.object({ id: z10.uuid(), name: z10.string(), year: z10.number().nullable() }).nullable(),
  venue: z10.object({ name: z10.string(), city: z10.string().nullable() }).nullable(),
  referee: matchRefereeSchema.nullable().default(null),
  minute: z10.number().int().nullable().default(null),
  period: z10.string().nullable().default(null),
  prices: matchPricesSchema.nullable().default(null),
  participants: z10.array(matchParticipantSchema),
  headToHead: z10.array(headToHeadMatchSchema)
});
var isSampleSize = (value) => SAMPLE_SIZES.includes(value);
var sampleSizeSchema = z10.coerce.number().int().refine(isSampleSize);
var venueFilterSchema = z10.enum(["all", "home", "away"]);
var matchVenueFilterSchema = z10.enum(["split", "all", "home", "away"]);
var competitionFilterSchema = z10.enum(["all", "current"]);
var teamMetricsQuerySchema = z10.object({
  sample: sampleSizeSchema.default(10),
  venue: matchVenueFilterSchema.default("all"),
  competition: competitionFilterSchema.default("all"),
  period: z10.string().min(1).default("full")
});
var teamMetricsRequestSchema = matchParamsSchema.extend(teamMetricsQuerySchema.shape).refine((request) => isPeriod(request.sport, request.period), { path: ["period"] });
var teamMetricSampleSchema = z10.object({
  metric: z10.string(),
  countable: z10.boolean(),
  values: z10.array(z10.number()),
  mean: z10.number().nullable(),
  coefficientOfVariation: z10.number().nullable(),
  sampleSize: z10.number().int(),
  eligibleCount: z10.number().int(),
  missingCount: z10.number().int(),
  from: z10.iso.date().nullable(),
  to: z10.iso.date().nullable()
});
var teamMetricsTeamSchema = z10.object({
  teamId: z10.uuid(),
  teamName: z10.string(),
  side: z10.enum(["home", "away"]),
  metrics: z10.array(teamMetricSampleSchema)
});
var teamMetricsResponseSchema = z10.object({
  filters: z10.object({
    sample: z10.number().int(),
    venue: matchVenueFilterSchema,
    competition: competitionFilterSchema,
    period: z10.string()
  }),
  periods: z10.array(z10.string()),
  teams: z10.array(teamMetricsTeamSchema)
});
var playerMetricsQuerySchema = z10.object({
  sample: sampleSizeSchema.default(10)
});
var playerMetricsRequestSchema = matchParamsSchema.extend(playerMetricsQuerySchema.shape);
var playerMetricSampleSchema = z10.object({
  metric: z10.string(),
  mean: z10.number().nullable(),
  sampleSize: z10.number().int()
});
var playerMetricsPlayerSchema = z10.object({
  playerId: z10.uuid(),
  playerName: z10.string(),
  position: z10.string().nullable(),
  appearances: z10.number().int(),
  metrics: z10.array(playerMetricSampleSchema)
});
var playerMetricsTeamSchema = z10.object({
  teamId: z10.uuid(),
  teamName: z10.string(),
  side: z10.enum(["home", "away"]),
  players: z10.array(playerMetricsPlayerSchema)
});
var playerMetricsResponseSchema = z10.object({
  filters: z10.object({ sample: z10.number().int() }),
  teams: z10.array(playerMetricsTeamSchema)
});
var lineQuoteSchema = z10.object({ price: z10.number(), capturedAt: z10.iso.datetime() });
var lineSideSchema = z10.object({
  side: z10.string(),
  price: z10.number(),
  capturedAt: z10.iso.datetime(),
  opening: lineQuoteSchema.nullable()
});
var lineHistorySchema = z10.object({
  hits: z10.number().int(),
  sample: z10.number().int(),
  label: z10.literal("history")
});
var marketLineSchema = z10.object({
  line: z10.number().nullable(),
  sides: z10.array(lineSideSchema),
  history: lineHistorySchema.nullable()
});
var linesMarketSchema = z10.object({
  key: z10.string(),
  label: z10.string().nullable(),
  lines: z10.array(marketLineSchema)
});
var linesResponseSchema = z10.object({
  bookmaker: z10.string().nullable(),
  markets: z10.array(linesMarketSchema)
});
var crossMetricSchema = z10.object({
  metric: z10.string(),
  produced: teamMetricSampleSchema,
  opponentConceded: teamMetricSampleSchema,
  projection: z10.number().nullable()
});
var crossMetricsTeamSchema = z10.object({
  teamId: z10.uuid(),
  teamName: z10.string(),
  side: z10.enum(["home", "away"]),
  metrics: z10.array(crossMetricSchema)
});
var crossMetricsResponseSchema = z10.object({
  filters: z10.object({
    sample: z10.number().int(),
    venue: matchVenueFilterSchema,
    competition: competitionFilterSchema,
    period: z10.string()
  }),
  teams: z10.array(crossMetricsTeamSchema)
});
var matchStatisticsTeamSchema = z10.object({
  teamId: z10.uuid(),
  teamName: z10.string(),
  side: z10.enum(["home", "away"])
});
var matchStatisticsRowSchema = z10.object({
  metric: z10.string(),
  home: z10.number().nullable(),
  away: z10.number().nullable()
});
var matchStatisticsPeriodSchema = z10.object({
  period: z10.string(),
  rows: z10.array(matchStatisticsRowSchema)
});
var matchStatisticsResponseSchema = z10.object({
  teams: z10.array(matchStatisticsTeamSchema),
  periods: z10.array(matchStatisticsPeriodSchema)
});
var matchRoutes = {
  list: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/matches",
    summary: "Stored fixtures for a local calendar day",
    auth: "user",
    params: z10.object({ sport: sportSchema }),
    query: matchesQuerySchema,
    response: matchesResponseSchema
  }),
  detail: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/matches/:id",
    summary: "One stored fixture with participants and head to head history",
    auth: "user",
    params: matchParamsSchema,
    response: matchDetailSchema
  }),
  teamMetrics: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/matches/:id/team-metrics",
    summary: "Recent form per team metric for both teams of a fixture",
    auth: "user",
    params: matchParamsSchema,
    query: teamMetricsQuerySchema,
    response: teamMetricsResponseSchema
  }),
  statistics: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/matches/:id/statistics",
    summary: "Recorded team statistics of a fixture by period",
    auth: "user",
    params: matchParamsSchema,
    response: matchStatisticsResponseSchema
  }),
  playerMetrics: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/matches/:id/player-metrics",
    summary: "Recent per game averages for every player of both teams of a fixture",
    auth: "user",
    params: matchParamsSchema,
    query: playerMetricsQuerySchema,
    response: playerMetricsResponseSchema
  }),
  lines: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/matches/:id/lines",
    summary: "Latest and opening bookmaker prices of a fixture by market and line",
    auth: "user",
    params: matchParamsSchema,
    response: linesResponseSchema
  }),
  crossMetrics: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/matches/:id/cross-metrics",
    summary: "What each team produces against what its opponent concedes",
    auth: "user",
    params: matchParamsSchema,
    query: teamMetricsQuerySchema,
    response: crossMetricsResponseSchema
  })
};

// src/reference.ts
import { z as z11 } from "zod";
var ANALYSIS_SAMPLE_SIZES = [3, 5, 10, 20];
var analysisSampleSchema = z11.coerce.number().int().refine((value) => ANALYSIS_SAMPLE_SIZES.includes(value)).default(10);
var sportParams = z11.object({ sport: sportSchema });
var idParams = z11.object({ sport: sportSchema, id: z11.uuid() });
var searchQuerySchema = z11.object({ q: z11.string().trim().min(2).max(60) });
var searchTeamSchema = z11.object({
  id: z11.uuid(),
  name: z11.string(),
  abbreviation: z11.string().nullable(),
  logoUrl: z11.string().nullable()
});
var searchPlayerSchema = z11.object({
  id: z11.uuid(),
  name: z11.string(),
  position: z11.string().nullable(),
  teamId: z11.uuid().nullable(),
  teamName: z11.string().nullable()
});
var searchCompetitionSchema = z11.object({
  id: z11.uuid(),
  name: z11.string(),
  logoUrl: z11.string().nullable().optional()
});
var searchMoreSchema = z11.object({
  competitions: z11.boolean(),
  teams: z11.boolean(),
  players: z11.boolean()
});
var searchResponseSchema = z11.object({
  query: z11.string(),
  competitions: z11.array(searchCompetitionSchema),
  teams: z11.array(searchTeamSchema),
  players: z11.array(searchPlayerSchema),
  more: searchMoreSchema
});
var seasonSchema = z11.object({
  id: z11.uuid(),
  name: z11.string(),
  year: z11.number().int().nullable()
});
var competitionReferenceSchema = z11.object({
  id: z11.uuid(),
  name: z11.string(),
  logoUrl: z11.string().nullable()
});
var competitionSchema = z11.object({
  id: z11.uuid(),
  name: z11.string(),
  countryCode: z11.string().nullable(),
  countryName: z11.string().nullable(),
  logoUrl: z11.string().nullable().optional(),
  standingsAsOf: z11.iso.date().nullable(),
  seasons: z11.array(seasonSchema)
});
var competitionsResponseSchema = z11.object({ items: z11.array(competitionSchema) });
var standingRowSchema = z11.object({
  rank: z11.number().int().nullable(),
  teamId: z11.uuid(),
  teamName: z11.string(),
  logoUrl: z11.string().nullable(),
  played: z11.number().int().nullable(),
  won: z11.number().int().nullable(),
  drawn: z11.number().int().nullable(),
  lost: z11.number().int().nullable(),
  scored: z11.number().int().nullable(),
  conceded: z11.number().int().nullable(),
  points: z11.number().int().nullable()
});
var standingsGroupSchema = z11.object({
  name: z11.string().nullable(),
  rows: z11.array(standingRowSchema)
});
var standingsResponseSchema = z11.object({
  season: seasonSchema,
  asOf: z11.iso.date(),
  groups: z11.array(standingsGroupSchema)
});
var competitionDetailSchema = z11.object({
  competition: competitionSchema,
  standings: standingsResponseSchema.nullable()
});
var competitionQuerySchema = z11.object({ season: z11.uuid().optional() });
var teamSummarySchema = z11.object({
  id: z11.uuid(),
  name: z11.string(),
  shortName: z11.string().nullable(),
  abbreviation: z11.string().nullable(),
  city: z11.string().nullable(),
  logoUrl: z11.string().nullable(),
  competitions: z11.array(z11.object({ id: z11.uuid(), name: z11.string() }))
});
var teamsQuerySchema = cursorQuerySchema.extend({
  q: z11.string().trim().max(60).optional(),
  competitionId: z11.uuid().optional()
});
var teamsResponseSchema = paginated(teamSummarySchema);
var formEntrySchema = z11.object({
  fixtureId: z11.uuid(),
  date: z11.iso.date(),
  opponentTeamId: z11.uuid(),
  opponentName: z11.string(),
  side: z11.enum(["home", "away"]),
  result: z11.enum(["won", "lost", "drawn"]).nullable(),
  score: z11.number().nullable(),
  opponentScore: z11.number().nullable()
});
var teamStandingSchema = z11.object({
  season: seasonSchema,
  competition: competitionReferenceSchema,
  asOf: z11.iso.date(),
  group: z11.string().nullable(),
  row: standingRowSchema
});
var teamDetailSchema = z11.object({
  team: teamSummarySchema,
  standing: teamStandingSchema.nullable(),
  form: z11.array(formEntrySchema),
  nextMatch: matchSchema.nullable(),
  lastMatch: matchSchema.nullable()
});
var teamStandingsResponseSchema = z11.object({ items: z11.array(teamStandingSchema) });
var teamResearchQuerySchema = z11.object({
  sample: sampleSizeSchema.default(10),
  venue: venueFilterSchema.default("all"),
  period: z11.string().min(1).default("full")
});
var teamResearchResponseSchema = z11.object({
  filters: teamResearchQuerySchema,
  periods: z11.array(z11.string()),
  metrics: z11.array(teamMetricSampleSchema)
});
var teamAnalysisQuerySchema = z11.object({
  sample: analysisSampleSchema,
  venue: venueFilterSchema.default("all"),
  competitionId: z11.uuid().optional()
});
var analysisValuesSchema = z11.record(z11.string(), z11.record(z11.string(), z11.number()));
var teamAnalysisGameSchema = z11.object({
  fixtureId: z11.uuid(),
  date: z11.iso.date(),
  side: z11.enum(["home", "away"]),
  opponentId: z11.uuid(),
  opponentName: z11.string(),
  opponentLogoUrl: z11.string().nullable(),
  competitionId: z11.uuid().nullable(),
  competitionName: z11.string().nullable(),
  competitionLogoUrl: z11.string().nullable(),
  scoredFirst: z11.boolean().nullable(),
  result: z11.enum(["won", "drawn", "lost"]).nullable().default(null),
  score: z11.number().nullable().default(null),
  opponentScore: z11.number().nullable().default(null),
  halfTimeScore: z11.number().nullable().default(null),
  halfTimeOpponentScore: z11.number().nullable().default(null),
  own: analysisValuesSchema,
  opponent: analysisValuesSchema
});
var teamAnalysisResponseSchema = z11.object({
  filters: teamAnalysisQuerySchema,
  competitions: z11.array(z11.object({ id: z11.uuid(), name: z11.string() })),
  games: z11.array(teamAnalysisGameSchema)
});
var teamFixturesQuerySchema = z11.object({
  season: z11.uuid().optional(),
  timezone: timezoneSchema.default("UTC")
});
var teamSeasonSchema = seasonSchema.extend({
  competition: z11.object({ id: z11.uuid(), name: z11.string() }).nullable().default(null)
});
var teamFixturesResponseSchema = z11.object({
  season: teamSeasonSchema.nullable(),
  seasons: z11.array(teamSeasonSchema),
  items: z11.array(matchSchema)
});
var rosterPlayerSchema = z11.object({
  imageUrl: z11.string().nullable().optional(),
  id: z11.uuid(),
  name: z11.string(),
  position: z11.string().nullable(),
  jerseyNumber: z11.string().nullable(),
  appearances: z11.number().int(),
  lastPlayedOn: z11.iso.date().nullable()
});
var teamPlayersResponseSchema = z11.object({
  season: seasonSchema.nullable(),
  items: z11.array(rosterPlayerSchema)
});
var playersQuerySchema = cursorQuerySchema.extend({
  q: z11.string().trim().max(60).optional(),
  teamId: z11.uuid().optional()
});
var playerSummarySchema = z11.object({
  imageUrl: z11.string().nullable().optional(),
  id: z11.uuid(),
  name: z11.string(),
  position: z11.string().nullable(),
  jerseyNumber: z11.string().nullable(),
  teamId: z11.uuid().nullable(),
  teamName: z11.string().nullable(),
  teamLogoUrl: z11.string().nullable()
});
var playersResponseSchema = paginated(playerSummarySchema);
var playerDetailSchema = z11.object({
  player: playerSummarySchema,
  birthDate: z11.iso.date().nullable(),
  countryCode: z11.string().nullable()
});
var playerAnalysisQuerySchema = z11.object({
  sample: analysisSampleSchema,
  venue: venueFilterSchema.default("all"),
  competitionId: z11.uuid().optional(),
  role: z11.enum(["all", "starter", "substitute", "batter", "pitcher"]).default("all"),
  minMinutes: z11.coerce.number().int().min(0).max(120).default(0)
});
var playerAnalysisGameSchema = z11.object({
  fixtureId: z11.uuid(),
  date: z11.iso.date(),
  teamId: z11.uuid(),
  teamName: z11.string(),
  opponentId: z11.uuid(),
  opponentName: z11.string(),
  opponentLogoUrl: z11.string().nullable(),
  side: z11.enum(["home", "away"]),
  score: z11.number().nullable(),
  opponentScore: z11.number().nullable(),
  competitionId: z11.uuid().nullable(),
  competitionName: z11.string().nullable(),
  started: z11.boolean().nullable(),
  battingOrder: z11.number().int().nullable().optional(),
  values: z11.record(z11.string(), z11.number())
});
var playerAnalysisResponseSchema = z11.object({
  filters: playerAnalysisQuerySchema,
  competitions: z11.array(z11.object({ id: z11.uuid(), name: z11.string() })),
  games: z11.array(playerAnalysisGameSchema)
});
var playerGamesQuerySchema = z11.object({
  sample: z11.union([sampleSizeSchema, z11.literal("all")]).default(10)
});
var playerMetricAverageSchema = z11.object({
  metric: z11.string(),
  mean: z11.number().nullable(),
  sampleSize: z11.number().int()
});
var playerGameSchema = z11.object({
  fixtureId: z11.uuid(),
  date: z11.iso.date(),
  teamId: z11.uuid(),
  opponentTeamId: z11.uuid(),
  opponentName: z11.string(),
  side: z11.enum(["home", "away"]),
  values: z11.record(z11.string(), z11.number())
});
var seasonPhaseSchema = z11.enum(["in_season", "pre_season", "off_season"]);
var sportStatusQuerySchema = z11.object({ timezone: timezoneSchema.default("UTC") });
var sportStatusResponseSchema = z11.object({
  phase: seasonPhaseSchema,
  today: z11.iso.date(),
  nextMatchOn: z11.iso.date().nullable(),
  lastMatchOn: z11.iso.date().nullable(),
  upcomingCount: z11.number().int()
});
var playerGamesResponseSchema = z11.object({
  filters: playerGamesQuerySchema,
  appearances: z11.number().int(),
  averages: z11.array(playerMetricAverageSchema),
  games: z11.array(playerGameSchema)
});
var referenceRoutes = {
  playerAnalysis: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/players/:id/analysis",
    summary: "Recent player appearances with match statistics and participation filters",
    auth: "user",
    params: idParams,
    query: playerAnalysisQuerySchema,
    response: playerAnalysisResponseSchema
  }),
  teamAnalysis: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/teams/:id/analysis",
    summary: "Recent team games with aligned statistics for both participants",
    auth: "user",
    params: idParams,
    query: teamAnalysisQuerySchema,
    response: teamAnalysisResponseSchema
  }),
  status: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/status",
    summary: "Season phase of a sport for a local calendar day",
    auth: "user",
    params: sportParams,
    query: sportStatusQuerySchema,
    response: sportStatusResponseSchema
  }),
  search: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/search",
    summary: "Competitions, teams and players matching a query",
    auth: "user",
    params: sportParams,
    query: searchQuerySchema,
    response: searchResponseSchema
  }),
  competitions: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/competitions",
    summary: "Competitions of a sport with their seasons",
    auth: "user",
    params: sportParams,
    response: competitionsResponseSchema
  }),
  competition: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/competitions/:id",
    summary: "One competition with the standings of a season",
    auth: "user",
    params: idParams,
    query: competitionQuerySchema,
    response: competitionDetailSchema
  }),
  teams: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/teams",
    summary: "Active teams of a sport",
    auth: "user",
    params: sportParams,
    query: teamsQuerySchema,
    response: teamsResponseSchema
  }),
  team: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/teams/:id",
    summary: "One team with standing, form and surrounding fixtures",
    auth: "user",
    params: idParams,
    response: teamDetailSchema
  }),
  teamMetrics: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/teams/:id/metrics",
    summary: "Recent per game team metrics",
    auth: "user",
    params: idParams,
    query: teamResearchQuerySchema,
    response: teamResearchResponseSchema
  }),
  teamStandings: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/teams/:id/standings",
    summary: "Latest team standing for each season and group",
    auth: "user",
    params: idParams,
    response: teamStandingsResponseSchema
  }),
  teamFixtures: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/teams/:id/fixtures",
    summary: "Fixtures of a team in a season",
    auth: "user",
    params: idParams,
    query: teamFixturesQuerySchema,
    response: teamFixturesResponseSchema
  }),
  teamPlayers: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/teams/:id/players",
    summary: "Players who appeared for a team this season",
    auth: "user",
    params: idParams,
    response: teamPlayersResponseSchema
  }),
  players: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/players",
    summary: "Players with recorded games",
    auth: "user",
    params: sportParams,
    query: playersQuerySchema,
    response: playersResponseSchema
  }),
  player: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/players/:id",
    summary: "One player",
    auth: "user",
    params: idParams,
    response: playerDetailSchema
  }),
  playerGames: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/players/:id/games",
    summary: "Recent games and per game averages of a player",
    auth: "user",
    params: idParams,
    query: playerGamesQuerySchema,
    response: playerGamesResponseSchema
  })
};

// src/match-analysis.ts
import { z as z12 } from "zod";
var closingQuoteSchema = z12.object({
  bookmaker: z12.string(),
  capturedAt: z12.iso.datetime(),
  price: z12.number().nullable(),
  draw: z12.number().nullable(),
  opponentPrice: z12.number().nullable(),
  spread: z12.object({ line: z12.number(), price: z12.number() }).nullable(),
  total: z12.object({ line: z12.number(), over: z12.number().nullable(), under: z12.number().nullable() }).nullable()
});
var matchAnalysisGameSchema = teamAnalysisGameSchema.extend({
  closing: closingQuoteSchema.nullable()
});
var matchAnalysisQuerySchema = z12.object({
  sample: sampleSizeSchema.default(10),
  venue: matchVenueFilterSchema.default("split"),
  competition: competitionFilterSchema.default("all")
});
var matchAnalysisRequestSchema = matchParamsSchema.extend(matchAnalysisQuerySchema.shape);
var matchAnalysisTeamSchema = z12.object({
  teamId: z12.uuid(),
  teamName: z12.string(),
  side: z12.enum(["home", "away"]),
  logoUrl: z12.string().nullable(),
  games: z12.array(matchAnalysisGameSchema)
});
var matchAnalysisResponseSchema = z12.object({
  filters: matchAnalysisQuerySchema,
  competition: z12.object({ id: z12.uuid(), name: z12.string() }).nullable(),
  teams: z12.array(matchAnalysisTeamSchema),
  headToHead: z12.object({ teamId: z12.uuid(), games: z12.array(matchAnalysisGameSchema) })
});
var matchAnalysisRoute = defineRoute({
  method: "GET",
  path: "/v1/sports/:sport/matches/:id/analysis",
  summary: "Last games of both teams with closing prices and head to head for one fixture",
  auth: "user",
  params: matchParamsSchema,
  query: matchAnalysisQuerySchema,
  response: matchAnalysisResponseSchema
});

// src/radar.ts
import { z as z13 } from "zod";
import { LINEUP_STATUSES } from "@pickpro/domain";
var FOOTBALL_MARKET_KEYS = [
  "result",
  "goals",
  "corners",
  "cards",
  "btts",
  "assists",
  "shots",
  "shots_on_target",
  "tackles",
  "fouls",
  "yellow_cards"
];
var TEAM_MARKET_KEYS = ["moneyline", "spread", "points", "runs"];
var NBA_PROP_METRIC_KEYS = [
  "points",
  "rebounds",
  "assists",
  "three_pointers_made",
  "steals",
  "blocks",
  "turnovers",
  "points_rebounds_assists",
  "points_rebounds",
  "points_assists",
  "rebounds_assists",
  "double_double",
  "triple_double"
];
var NFL_PROP_METRIC_KEYS = [
  "passing_yards",
  "passing_touchdowns",
  "passing_completions",
  "passing_attempts",
  "passing_interceptions",
  "rushing_yards",
  "rushing_attempts",
  "receiving_yards",
  "receptions",
  "rushing_receiving_yards",
  "anytime_touchdown",
  "first_touchdown",
  "longest_reception",
  "longest_rush",
  "longest_pass",
  "kicking_points",
  "field_goals_made"
];
var MLB_PROP_METRIC_KEYS = [
  "hits",
  "singles",
  "doubles",
  "triples",
  "home_runs",
  "total_bases",
  "extra_base_hits",
  "runs",
  "rbi",
  "walks",
  "strikeouts",
  "stolen_bases",
  "hits_runs_rbis",
  "runs_rbis",
  "hits_stolen_bases",
  "hits_runs_stolen_bases",
  "hits_walks_stolen_bases",
  "first_home_run",
  "pitching_strikeouts",
  "pitcher_outs",
  "pitcher_earned_runs",
  "pitcher_hits_allowed",
  "pitching_walks",
  "pitcher_win",
  "pitcher_hits_walks_earned_runs"
];
var FOOTBALL_PROP_METRIC_KEYS = [
  "goals",
  "assists",
  "shots",
  "shots_on_target",
  "tackles",
  "fouls",
  "yellow_cards"
];
var radarMarketVocabulary = [
  .../* @__PURE__ */ new Set([
    ...FOOTBALL_MARKET_KEYS,
    ...TEAM_MARKET_KEYS,
    ...NBA_PROP_METRIC_KEYS,
    ...NFL_PROP_METRIC_KEYS,
    ...MLB_PROP_METRIC_KEYS
  ])
];
var radarMarketPattern = radarMarketVocabulary.join("|");
var radarMarketMaxLength = radarMarketVocabulary.join(",").length;
var atMostOneScope = (value, ctx) => {
  const present = ["fixture", "team", "player"].filter(
    (key) => value[key] !== void 0
  );
  if (present.length < 2) return;
  for (const key of present) {
    ctx.addIssue({
      code: "custom",
      path: [key],
      message: "at most one of fixture, team or player"
    });
  }
};
var radarQueryBaseSchema = z13.object({
  date: z13.iso.date().optional(),
  timezone: timezoneSchema.default("UTC"),
  q: z13.string().trim().max(200).optional(),
  competition: z13.string().max(3699).refine((value) => value.split(",").every((id) => z13.uuid().safeParse(id).success)).optional(),
  fixture: z13.uuid().optional(),
  team: z13.uuid().optional(),
  player: z13.uuid().optional(),
  market: z13.string().max(radarMarketMaxLength).regex(new RegExp(`^(${radarMarketPattern})(,(${radarMarketPattern}))*$`)).optional(),
  sort: z13.enum(["value", "probability", "kickoff", "hitRate"]).optional(),
  positive: z13.enum(["true", "false"]).optional(),
  includeTeams: z13.enum(["true", "false"]).optional(),
  period: z13.enum(["full", "first_half"]).optional(),
  side: z13.enum(["over", "under"]).optional(),
  minProbability: z13.coerce.number().min(0).max(1).optional(),
  probabilityBasis: z13.enum(["estimate", "hitRate"]).optional(),
  minOdds: z13.coerce.number().min(1).optional(),
  page: z13.coerce.number().int().min(1).max(1e4).optional(),
  pageSize: z13.coerce.number().int().min(1).max(100).optional()
});
var radarQuerySchema = radarQueryBaseSchema.superRefine(atMostOneScope);
var pageInfo = {
  total: z13.number().int().nonnegative(),
  page: z13.number().int().positive(),
  pageSize: z13.number().int().positive(),
  competitions: z13.array(
    z13.object({ id: z13.uuid(), name: z13.string(), logoUrl: z13.string().nullable().optional() })
  )
};
var radarFixtureFields = {
  fixtureId: z13.uuid(),
  date: z13.iso.date(),
  startsAt: z13.iso.datetime(),
  competitionId: z13.uuid().nullable(),
  competition: z13.string(),
  competitionLogoUrl: z13.string().nullable().optional(),
  homeName: z13.string(),
  awayName: z13.string(),
  homeLogoUrl: z13.string().nullable(),
  awayLogoUrl: z13.string().nullable(),
  homeTeamId: z13.uuid().nullable().optional(),
  awayTeamId: z13.uuid().nullable().optional()
};
var playerAvailabilityStatusSchema = z13.enum([
  "out",
  "doubtful",
  "questionable",
  "day_to_day",
  "probable",
  "active"
]);
var radarWarningSchema = z13.discriminatedUnion("kind", [
  z13.object({ kind: z13.literal("neutral_venue") }),
  z13.object({ kind: z13.literal("short_rest"), team: z13.string(), hours: z13.number().nonnegative() }),
  z13.object({ kind: z13.literal("absences"), team: z13.string(), count: z13.number().int().positive() }),
  z13.object({
    kind: z13.literal("referee_cards"),
    average: z13.number().nonnegative(),
    sample: z13.number().int().min(10).max(20)
  }),
  z13.object({
    kind: z13.literal("strong_favorite"),
    team: z13.string(),
    probability: z13.number().min(0.7).max(1)
  }),
  z13.object({
    kind: z13.literal("injury"),
    players: z13.array(
      z13.object({
        name: z13.string(),
        status: playerAvailabilityStatusSchema,
        detail: z13.string().nullable()
      })
    ).min(1).max(3)
  }),
  z13.object({ kind: z13.literal("lineup_pending") })
]);
var radarItemSchema = z13.object({
  ...radarFixtureFields,
  id: z13.string(),
  market: z13.enum(["result", "goals", "corners", "btts", "moneyline", "spread", "points", "runs"]),
  period: z13.enum(["full", "first_half"]),
  side: z13.enum(["home", "draw", "away", "over", "under", "yes", "no"]),
  line: z13.number().nullable(),
  probability: z13.number().min(0).max(1),
  pushProbability: z13.number().min(0).max(1),
  price: z13.number().gt(1),
  fairOdds: z13.number().positive(),
  expectedValue: z13.number(),
  bookmaker: z13.string(),
  source: z13.enum(["prediction", "value_bet"]),
  teamSide: z13.enum(["home", "away"]).optional(),
  teamName: z13.string().optional(),
  observedAt: z13.iso.datetime(),
  oddsAt: z13.iso.datetime(),
  openingPrice: z13.number().gt(1).nullable().optional(),
  previousPrice: z13.number().gt(1).nullable().optional(),
  previousOddsAt: z13.iso.datetime().nullable().optional(),
  warnings: z13.array(radarWarningSchema).max(5).optional()
});
var radarHistoryGameSchema = z13.object({
  fixtureId: z13.uuid(),
  startsAt: z13.iso.datetime(),
  value: z13.number().nonnegative().nullable(),
  opponentName: z13.string().optional(),
  opponentLogoUrl: z13.string().nullable().optional(),
  teamSide: z13.enum(["home", "away"]).optional(),
  teamScore: z13.number().nonnegative().nullable().optional(),
  opponentScore: z13.number().nonnegative().nullable().optional(),
  quarters: z13.array(z13.object({ teamScore: z13.number().nullable(), opponentScore: z13.number().nullable() })).max(6).optional()
});
var teamRadarItemSchema = radarItemSchema.extend({
  market: z13.enum(["goals", "corners", "cards", "shots", "shots_on_target", "moneyline", "spread"]),
  period: z13.literal("full"),
  side: z13.enum(["over", "under", "home", "away"]),
  line: z13.number().nullable(),
  source: z13.literal("history"),
  teamId: z13.uuid(),
  teamName: z13.string(),
  teamSide: z13.enum(["home", "away"]),
  hits: z13.number().int().nonnegative(),
  known: z13.number().int().min(5),
  total: z13.number().int().min(5).max(10),
  average: z13.number().nonnegative(),
  history: z13.array(radarHistoryGameSchema).max(10).optional(),
  opponentHistory: z13.array(radarHistoryGameSchema).max(10).optional()
});
var matchHistoryRadarItemSchema = teamRadarItemSchema.omit({ teamId: true, teamName: true, teamSide: true }).extend({
  scope: z13.literal("match"),
  market: z13.enum(["goals", "corners", "cards", "shots", "shots_on_target", "points", "runs"]),
  side: z13.enum(["over", "under"]),
  line: z13.number().nonnegative(),
  period: z13.enum(["full", "first_half"]),
  total: z13.number().int().min(5).max(20)
});
var radarSelectionItemSchema = z13.union([
  radarItemSchema,
  teamRadarItemSchema,
  matchHistoryRadarItemSchema
]);
var radarResponseSchema = z13.object({
  ...pageInfo,
  items: z13.array(radarSelectionItemSchema),
  fixtures: z13.number().int().nonnegative(),
  analyzedFixtures: z13.number().int().nonnegative(),
  updatedAt: z13.iso.datetime().nullable()
});
var playerRadarOddsSchema = z13.object({
  price: z13.number().gt(1),
  bookmaker: z13.string(),
  observedAt: z13.iso.datetime(),
  updatedAt: z13.iso.datetime(),
  over: z13.number().gt(1).nullable().optional(),
  under: z13.number().gt(1).nullable().optional(),
  opening: z13.object({ over: z13.number().gt(1).nullable(), under: z13.number().gt(1).nullable() }).nullable().optional(),
  oddsAt: z13.iso.datetime().optional()
});
var playerRadarLineupSchema = z13.object({
  starter: z13.boolean().nullable(),
  battingOrder: z13.number().int().positive().nullable(),
  probablePitcher: z13.boolean().nullable(),
  confirmed: z13.boolean().optional()
});
var lineupStatusSchema = z13.enum(LINEUP_STATUSES);
var playerRadarAvailabilitySchema = z13.object({
  status: playerAvailabilityStatusSchema,
  rawStatus: z13.string().nullable(),
  detail: z13.string().nullable(),
  returnDate: z13.iso.date().nullable()
});
var playerRadarVersusSchema = z13.object({
  plateAppearances: z13.number().int().nonnegative(),
  atBats: z13.number().int().nonnegative(),
  hits: z13.number().int().nonnegative(),
  homeRuns: z13.number().int().nonnegative(),
  strikeouts: z13.number().int().nonnegative(),
  walks: z13.number().int().nonnegative(),
  avg: z13.number().nullable(),
  ops: z13.number().nullable(),
  opponentName: z13.string()
});
var playerRadarSplitLineSchema = z13.object({
  avg: z13.number().nullable(),
  ops: z13.number().nullable(),
  homeRuns: z13.number().nullable(),
  strikeouts: z13.number().nullable(),
  atBats: z13.number().nullable()
});
var playerRadarSplitsSchema = z13.object({
  homeAway: playerRadarSplitLineSchema.nullable(),
  versusHand: playerRadarSplitLineSchema.extend({ hand: z13.enum(["left", "right"]) }).nullable(),
  last15: playerRadarSplitLineSchema.nullable(),
  versusOpponent: playerRadarSplitLineSchema.nullable()
});
var PLAYER_RADAR_METRIC_KEYS = [
  "goals",
  "assists",
  "shots",
  "shots_on_target",
  "tackles",
  "fouls",
  "yellow_cards",
  "points",
  "rebounds",
  "three_pointers_made",
  "steals",
  "blocks",
  "turnovers",
  "points_rebounds_assists",
  "points_rebounds",
  "points_assists",
  "rebounds_assists",
  "double_double",
  "triple_double",
  "passing_yards",
  "passing_touchdowns",
  "passing_completions",
  "passing_attempts",
  "passing_interceptions",
  "rushing_yards",
  "rushing_attempts",
  "receiving_yards",
  "receptions",
  "rushing_receiving_yards",
  "anytime_touchdown",
  "first_touchdown",
  "longest_reception",
  "longest_rush",
  "longest_pass",
  "kicking_points",
  "field_goals_made",
  "hits",
  "singles",
  "doubles",
  "triples",
  "home_runs",
  "total_bases",
  "extra_base_hits",
  "runs",
  "rbi",
  "walks",
  "strikeouts",
  "stolen_bases",
  "hits_runs_rbis",
  "runs_rbis",
  "hits_stolen_bases",
  "hits_runs_stolen_bases",
  "hits_walks_stolen_bases",
  "first_home_run",
  "pitching_strikeouts",
  "pitcher_outs",
  "pitcher_earned_runs",
  "pitcher_hits_allowed",
  "pitching_walks",
  "pitcher_win",
  "pitcher_hits_walks_earned_runs"
];
var playerRadarItemSchema = z13.object({
  ...radarFixtureFields,
  id: z13.string(),
  playerId: z13.uuid(),
  playerName: z13.string(),
  imageUrl: z13.string().nullable(),
  teamName: z13.string(),
  teamId: z13.uuid(),
  teamSide: z13.enum(["home", "away"]),
  metric: z13.enum(PLAYER_RADAR_METRIC_KEYS),
  line: z13.number(),
  hits: z13.number().int(),
  known: z13.number().int(),
  total: z13.number().int(),
  missing: z13.number().int(),
  average: z13.number().nullable(),
  rate: z13.number().nullable(),
  averageMinutes: z13.number().nullable(),
  starts: z13.number().int(),
  values: z13.array(z13.number().nullable()),
  probability: z13.number().min(0).max(1),
  fairOdds: z13.number().positive(),
  expectedValue: z13.number(),
  odds: playerRadarOddsSchema,
  lineup: playerRadarLineupSchema.nullable().optional(),
  availability: playerRadarAvailabilitySchema.nullable().optional(),
  lineupStatus: lineupStatusSchema.optional(),
  versus: playerRadarVersusSchema.nullable().optional(),
  splits: playerRadarSplitsSchema.optional(),
  warnings: z13.array(radarWarningSchema).max(5).optional()
});
var playerRadarResponseSchema = z13.object({
  ...pageInfo,
  items: z13.array(playerRadarItemSchema),
  fixtures: z13.number().int().nonnegative()
});
var params = z13.object({ sport: sportSchema });
var radarRoutes = {
  list: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/radar",
    summary: "Model and historical opportunities with available prices",
    auth: "user",
    params,
    query: radarQuerySchema,
    response: radarResponseSchema
  }),
  players: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/radar/players",
    summary: "Player research trends and priced props for upcoming matches",
    auth: "user",
    params,
    query: radarQuerySchema,
    response: playerRadarResponseSchema
  })
};

// src/team-radar.ts
import { z as z14 } from "zod";
var teamRadarQuerySchema = radarQueryBaseSchema.pick({
  date: true,
  timezone: true,
  page: true,
  pageSize: true,
  fixture: true,
  team: true,
  player: true
}).superRefine(atMostOneScope);
var teamRadarResponseSchema = radarResponseSchema.extend({
  items: z14.array(teamRadarItemSchema)
});
var teamRadarRoute = defineRoute({
  method: "GET",
  path: "/v1/sports/:sport/radar/teams",
  summary: "Team totals with available prices and home or away historical estimates",
  auth: "user",
  params: z14.object({ sport: sportSchema }),
  query: teamRadarQuerySchema,
  response: teamRadarResponseSchema
});

// src/tickets.ts
import { z as z15 } from "zod";
var ticketMarketSchema = z15.enum([
  "result",
  "goals",
  "corners",
  "btts",
  "cards",
  "shots",
  "shots_on_target",
  "points",
  "runs",
  "moneyline",
  "spread"
]);
var selectionResultSchema = z15.enum([
  "pending",
  "won",
  "lost",
  "void",
  "halfWon",
  "halfLost"
]);
var ticketSelectionSchema = radarItemSchema.extend({
  sport: sportSchema.optional(),
  market: ticketMarketSchema,
  period: z15.enum(["full", "first_half", "second_half"]),
  source: z15.enum(["prediction", "value_bet", "history"]),
  teamId: z15.uuid().optional(),
  teamName: z15.string().optional(),
  teamSide: z15.enum(["home", "away"]).optional(),
  hits: z15.number().int().nonnegative().optional(),
  known: z15.number().int().nonnegative().optional(),
  total: z15.number().int().nonnegative().optional(),
  average: z15.number().nullable().optional(),
  history: z15.array(radarHistoryGameSchema).max(10).optional(),
  opponentHistory: z15.array(radarHistoryGameSchema).max(10).optional()
});
var matchLegSchema = ticketSelectionSchema.extend({
  kind: z15.literal("match").default("match")
});
var playerLegSchema = z15.object({
  ...radarFixtureFields,
  id: z15.string(),
  kind: z15.literal("player"),
  sport: sportSchema,
  playerId: z15.uuid(),
  playerName: z15.string(),
  imageUrl: z15.string().nullable(),
  teamId: z15.uuid(),
  teamName: z15.string(),
  teamSide: z15.enum(["home", "away"]),
  metric: z15.enum(PLAYER_RADAR_METRIC_KEYS),
  line: z15.number(),
  side: z15.enum(["over", "under"]),
  period: z15.literal("full"),
  price: z15.number().gt(1),
  bookmaker: z15.string(),
  observedAt: z15.iso.datetime(),
  oddsAt: z15.iso.datetime(),
  openingPrice: z15.number().gt(1).nullable().optional(),
  previousPrice: z15.number().gt(1).nullable().optional(),
  previousOddsAt: z15.iso.datetime().nullable().optional(),
  probability: z15.number().min(0).max(1),
  pushProbability: z15.number().min(0).max(1),
  fairOdds: z15.number().positive(),
  expectedValue: z15.number(),
  source: z15.literal("history"),
  hits: z15.number().int().nonnegative(),
  known: z15.number().int().nonnegative(),
  total: z15.number().int().nonnegative(),
  values: z15.array(z15.number().nullable()).max(10),
  lineup: playerRadarLineupSchema.nullable().optional(),
  availability: playerRadarAvailabilitySchema.nullable().optional(),
  lineupStatus: lineupStatusSchema.optional(),
  warnings: z15.array(radarWarningSchema).max(5).optional()
});
var betLegSchema = z15.discriminatedUnion("kind", [matchLegSchema, playerLegSchema]);
var ticketItemsSchema = z15.array(betLegSchema).min(1).max(10).refine((items) => new Set(items.map((item) => item.id)).size === items.length);
var saveTicketSchema = z15.object({
  id: z15.uuid().optional(),
  sport: sportSchema,
  title: z15.string().trim().max(100).default(""),
  stake: z15.number().finite().min(0).max(1e6),
  items: ticketItemsSchema
});
var updateTicketSchema = saveTicketSchema.pick({ title: true, stake: true, items: true }).partial().extend({
  title: z15.string().trim().max(100).optional(),
  results: z15.record(z15.string(), selectionResultSchema).optional(),
  version: z15.number().int().positive().optional()
});
var savedTicketSchema = saveTicketSchema.extend({
  id: z15.uuid(),
  createdAt: z15.iso.datetime(),
  updatedAt: z15.iso.datetime(),
  version: z15.number().int(),
  results: z15.record(z15.string(), selectionResultSchema),
  status: z15.enum(["pending", "won", "lost", "void"]),
  payout: z15.number().nullable(),
  profit: z15.number().nullable()
});
var ticketListQuerySchema = z15.object({
  page: z15.coerce.number().int().min(1).default(1),
  pageSize: z15.coerce.number().int().min(1).max(100).default(20)
});
var ticketOpportunityQuerySchema = z15.object({
  date: z15.iso.date(),
  criterion: z15.enum(["match", "team"]).default("match"),
  page: z15.coerce.number().int().min(1).default(1),
  pageSize: z15.coerce.number().int().min(1).max(100).default(100)
});
var ticketWindowQuerySchema = z15.object({
  hours: z15.coerce.number().int().min(1).max(48),
  criterion: z15.enum(["match", "team"]).default("match")
});
var ticketRoutes = {
  window: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/ticket-opportunities/window",
    summary: "Complete ticket opportunities for the selected time window",
    auth: "user",
    params: z15.object({ sport: sportSchema }),
    query: ticketWindowQuerySchema,
    response: z15.object({ items: z15.array(betLegSchema), total: z15.number() })
  }),
  list: defineRoute({
    method: "GET",
    path: "/v1/tickets",
    summary: "Account notebook",
    auth: "user",
    query: ticketListQuerySchema,
    response: z15.object({
      items: z15.array(savedTicketSchema),
      total: z15.number(),
      page: z15.number(),
      pageSize: z15.number()
    })
  }),
  save: defineRoute({
    method: "POST",
    path: "/v1/tickets",
    summary: "Save ticket to account",
    auth: "user",
    body: saveTicketSchema,
    successStatus: 201,
    response: savedTicketSchema
  }),
  update: defineRoute({
    method: "PATCH",
    path: "/v1/tickets/:id",
    summary: "Edit ticket and selection results",
    auth: "user",
    params: idParamsSchema,
    body: updateTicketSchema,
    response: savedTicketSchema
  }),
  remove: defineRoute({
    method: "DELETE",
    path: "/v1/tickets/:id",
    summary: "Delete saved ticket",
    auth: "user",
    params: idParamsSchema,
    response: z15.null()
  }),
  settle: defineRoute({
    method: "POST",
    path: "/v1/tickets/:id/settle",
    summary: "Check results against recorded match statistics",
    auth: "user",
    params: idParamsSchema,
    response: savedTicketSchema
  }),
  opportunities: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/ticket-opportunities",
    summary: "Priced opportunities for each supported sport",
    auth: "user",
    params: z15.object({ sport: sportSchema }),
    query: ticketOpportunityQuerySchema,
    response: z15.object({
      items: z15.array(betLegSchema),
      total: z15.number(),
      page: z15.number(),
      pageSize: z15.number()
    })
  })
};

// src/portfolio.ts
import { z as z16 } from "zod";
import { playerPropMetricKeys, selectionIdentity } from "@pickpro/domain";
var historySchema = z16.array(
  z16.object({ fixtureId: z16.string(), startsAt: z16.iso.datetime(), value: z16.number().nullable() })
).max(20);
var researchMatchSchema = matchLegSchema.extend({
  price: z16.number().gt(1).nullable(),
  scope: z16.enum(["match", "team"]).optional(),
  history: historySchema.optional(),
  opponentHistory: historySchema.optional()
});
var researchPlayerSchema = playerLegSchema.extend({ price: z16.number().gt(1).nullable() });
var researchSelectionSchema = z16.discriminatedUnion("kind", [
  researchMatchSchema,
  researchPlayerSchema
]);
var notebookSaveSchema = z16.object({
  sport: sportSchema,
  items: z16.array(researchSelectionSchema).min(1).max(100)
});
var notebookEntrySchema = z16.object({
  id: z16.uuid(),
  sport: sportSchema,
  selection: researchSelectionSchema,
  createdAt: z16.iso.datetime()
});
var sportQuery = z16.object({ sport: sportSchema.optional() });
var minorSchema = z16.number().int().min(0).max(1e12);
var bankrollCreateSchema = z16.object({
  name: z16.string().trim().min(1).max(100),
  openingMinor: minorSchema
});
var bankrollUpdateSchema = z16.object({
  name: z16.string().trim().min(1).max(100).optional(),
  archived: z16.boolean().optional()
});
var bankrollAdjustmentSchema = z16.object({
  kind: z16.enum(["deposit", "withdrawal"]),
  amountMinor: minorSchema.min(1)
});
var bankrollSchema = z16.object({
  id: z16.uuid(),
  name: z16.string(),
  currency: z16.literal("BRL"),
  balanceMinor: z16.number(),
  archived: z16.boolean()
});
var stakeSchema = minorSchema.min(1).max(1e8);
var priceSchema = z16.number().gt(1).max(1e3);
var betItemSchema = z16.discriminatedUnion("kind", [
  researchMatchSchema.extend({ price: priceSchema }),
  researchPlayerSchema.extend({ price: priceSchema })
]);
var betCreateSchema = z16.object({
  requestId: z16.uuid(),
  bankrollId: z16.uuid(),
  sport: sportSchema,
  mode: z16.enum(["singles", "accumulator"]),
  stakeMinor: stakeSchema,
  bookmaker: z16.string().trim().min(1).max(100),
  items: z16.array(betItemSchema).min(1).max(10).refine((items) => new Set(items.map(selectionIdentity)).size === items.length)
}).refine((body) => body.items.every((item) => !item.sport || item.sport === body.sport)).refine(
  (body) => body.mode === "singles" || new Set(body.items.map((item) => item.fixtureId)).size === body.items.length
).superRefine((body, ctx) => {
  const metrics = new Set(playerPropMetricKeys(body.sport));
  body.items.forEach((item, index) => {
    if (item.kind === "player" && !metrics.has(item.metric))
      ctx.addIssue({
        code: "custom",
        path: ["items", index, "metric"],
        message: "metric is not a player prop for this sport"
      });
  });
});
var betUpdateSchema = z16.object({
  version: z16.number().int().positive(),
  results: z16.record(z16.string(), selectionResultSchema),
  stakeMinor: stakeSchema.optional(),
  prices: z16.record(z16.string(), priceSchema).optional()
});
var betSchema = z16.object({
  id: z16.uuid(),
  bankrollId: z16.uuid(),
  sport: sportSchema,
  mode: z16.enum(["singles", "accumulator"]),
  stakeMinor: z16.number(),
  bookmaker: z16.string(),
  items: z16.array(betLegSchema),
  results: z16.record(z16.string(), selectionResultSchema),
  status: z16.enum(["pending", "won", "lost", "void"]),
  payoutMinor: z16.number().nullable(),
  profitMinor: z16.number().nullable(),
  version: z16.number(),
  placedAt: z16.iso.datetime(),
  settledAt: z16.iso.datetime().nullable(),
  manual: z16.boolean(),
  needsReview: z16.boolean()
});
var openSelectionSchema = z16.object({
  selectionId: z16.string(),
  betId: z16.uuid(),
  bankrollId: z16.uuid(),
  bankrollName: z16.string(),
  stakeMinor: z16.number(),
  mode: z16.enum(["singles", "accumulator"])
});
var trackerTabSchema = z16.enum(["live", "upcoming", "settled"]);
var trackerQuerySchema = cursorQuerySchema.extend({ tab: trackerTabSchema });
var trackedMatchSchema = z16.object({
  id: z16.uuid(),
  sport: sportSchema,
  date: z16.iso.date(),
  startsAt: z16.iso.datetime().nullable(),
  status: matchStatusSchema,
  minute: z16.number().int().nullable(),
  period: z16.string().nullable(),
  competition: z16.string(),
  competitionId: z16.uuid().nullable(),
  competitionLogoUrl: z16.string().nullable(),
  countryCode: z16.string().nullable(),
  homeTeamId: z16.uuid(),
  homeName: z16.string(),
  homeLogoUrl: z16.string().nullable(),
  homeScore: z16.number().nullable(),
  awayTeamId: z16.uuid(),
  awayName: z16.string(),
  awayLogoUrl: z16.string().nullable(),
  awayScore: z16.number().nullable()
});
var legProgressSchema = z16.object({
  current: z16.number().nullable(),
  line: z16.number().nullable(),
  provisional: selectionResultSchema.nullable(),
  decided: z16.boolean()
});
var trackedLegSchema = z16.object({
  id: z16.string(),
  result: selectionResultSchema,
  match: trackedMatchSchema.nullable(),
  progress: legProgressSchema,
  lineupStatus: lineupStatusSchema.nullable()
});
var trackedBetSchema = betSchema.extend({
  awaiting: z16.boolean(),
  legs: z16.array(trackedLegSchema)
});
var trackerResponseSchema = paginated(trackedBetSchema);
var trackerSummarySchema = z16.object({
  live: z16.number().int().nonnegative(),
  upcoming: z16.number().int().nonnegative(),
  settled: z16.number().int().nonnegative()
});
var performanceQuerySchema = z16.object({
  sport: sportSchema,
  timezone: timezoneSchema.default("UTC"),
  from: z16.iso.date().optional(),
  to: z16.iso.date().optional(),
  market: ticketMarketSchema.optional()
}).refine((query) => !query.from || !query.to || query.from <= query.to);
var performanceSchema = z16.object({
  profitMinor: z16.number(),
  resolvedStakeMinor: z16.number(),
  roi: z16.number().nullable(),
  hitRate: z16.number().nullable(),
  resolved: z16.number(),
  pending: z16.number(),
  riskMinor: z16.number(),
  live: z16.number(),
  series: z16.array(z16.object({ date: z16.string(), profitMinor: z16.number() })),
  updatedAt: z16.iso.datetime()
});
var notebookRoutes = {
  list: defineRoute({
    method: "GET",
    path: "/v1/notebook",
    auth: "user",
    summary: "Saved research selections",
    query: sportQuery,
    response: z16.object({ items: z16.array(notebookEntrySchema) })
  }),
  save: defineRoute({
    method: "POST",
    path: "/v1/notebook",
    auth: "user",
    summary: "Save research selections",
    body: notebookSaveSchema,
    successStatus: 201,
    response: z16.null()
  }),
  remove: defineRoute({
    method: "DELETE",
    path: "/v1/notebook/:id",
    auth: "user",
    summary: "Remove saved research",
    params: idParamsSchema,
    response: z16.null()
  }),
  clear: defineRoute({
    method: "DELETE",
    path: "/v1/notebook",
    auth: "user",
    summary: "Clear saved research",
    query: sportQuery,
    response: z16.null()
  })
};
var portfolioRoutes = {
  bankrolls: defineRoute({
    method: "GET",
    path: "/v1/bankrolls",
    auth: "user",
    summary: "Account bankrolls",
    response: z16.object({ items: z16.array(bankrollSchema) })
  }),
  createBankroll: defineRoute({
    method: "POST",
    path: "/v1/bankrolls",
    auth: "user",
    summary: "Create bankroll",
    body: bankrollCreateSchema,
    successStatus: 201,
    response: bankrollSchema
  }),
  updateBankroll: defineRoute({
    method: "PATCH",
    path: "/v1/bankrolls/:id",
    auth: "user",
    summary: "Rename or archive bankroll",
    params: idParamsSchema,
    body: bankrollUpdateSchema,
    response: bankrollSchema
  }),
  adjustBankroll: defineRoute({
    method: "POST",
    path: "/v1/bankrolls/:id/adjustments",
    auth: "user",
    summary: "Deposit to or withdraw from a bankroll",
    params: idParamsSchema,
    body: bankrollAdjustmentSchema,
    response: bankrollSchema
  }),
  transactions: defineRoute({
    method: "GET",
    path: "/v1/bankrolls/:id/transactions",
    auth: "user",
    summary: "Bankroll transaction history",
    params: idParamsSchema,
    response: z16.object({
      items: z16.array(
        z16.object({
          id: z16.uuid(),
          amountMinor: z16.number(),
          kind: z16.string(),
          createdAt: z16.iso.datetime(),
          betId: z16.uuid().nullable()
        })
      )
    })
  }),
  bets: defineRoute({
    method: "GET",
    path: "/v1/bets",
    auth: "user",
    summary: "Recorded bets",
    query: sportQuery,
    response: z16.object({ items: z16.array(betSchema) })
  }),
  createBets: defineRoute({
    method: "POST",
    path: "/v1/bets",
    auth: "user",
    summary: "Record singles or accumulator",
    body: betCreateSchema,
    successStatus: 201,
    response: z16.object({ items: z16.array(betSchema) })
  }),
  openSelections: defineRoute({
    method: "GET",
    path: "/v1/bets/open-selections",
    auth: "user",
    summary: "Selections held by pending bets",
    query: z16.object({ sport: sportSchema }),
    response: z16.object({ items: z16.array(openSelectionSchema) })
  }),
  getBet: defineRoute({
    method: "GET",
    path: "/v1/bets/:id",
    auth: "user",
    summary: "One bet with its review state",
    params: idParamsSchema,
    response: betSchema
  }),
  updateBet: defineRoute({
    method: "PATCH",
    path: "/v1/bets/:id",
    auth: "user",
    summary: "Correct bet results",
    params: idParamsSchema,
    body: betUpdateSchema,
    response: betSchema
  }),
  settleBet: defineRoute({
    method: "POST",
    path: "/v1/bets/:id/settle",
    auth: "user",
    summary: "Check recorded match results",
    params: idParamsSchema,
    response: betSchema
  }),
  deleteBet: defineRoute({
    method: "DELETE",
    path: "/v1/bets/:id",
    auth: "user",
    summary: "Delete a bet and reverse its bankroll effect",
    params: idParamsSchema,
    response: z16.null()
  }),
  tracker: defineRoute({
    method: "GET",
    path: "/v1/bets/tracker",
    auth: "user",
    summary: "Bets grouped as live, upcoming or settled",
    query: trackerQuerySchema,
    response: trackerResponseSchema
  }),
  trackerSummary: defineRoute({
    method: "GET",
    path: "/v1/bets/tracker/summary",
    auth: "user",
    summary: "Bet counts per tracker tab",
    response: trackerSummarySchema
  }),
  performance: defineRoute({
    method: "GET",
    path: "/v1/bets/performance",
    auth: "user",
    summary: "Account performance for a period",
    query: performanceQuerySchema,
    response: performanceSchema
  })
};

// src/today.ts
import { z as z17 } from "zod";
var todayQuerySchema = z17.object({ timezone: timezoneSchema.default("UTC") });
var todayMatchSchema = matchSchema.extend({ favourite: z17.boolean() });
var todayResponseSchema = z17.object({
  date: z17.iso.date(),
  live: z17.array(todayMatchSchema),
  upcoming: z17.array(todayMatchSchema),
  counts: z17.object({
    live: z17.number().int(),
    upcoming: z17.number().int(),
    finished: z17.number().int()
  }),
  next: z17.object({ date: z17.iso.date(), count: z17.number().int(), matches: z17.array(todayMatchSchema) }).nullable(),
  phase: seasonPhaseSchema,
  sync: matchesResponseSchema.shape.sync
});
var todayRoutes = {
  get: defineRoute({
    method: "GET",
    path: "/v1/sports/:sport/today",
    summary: "Today's live and upcoming matches for a sport, favourites first",
    auth: "user",
    params: z17.object({ sport: sportSchema }),
    query: todayQuerySchema,
    response: todayResponseSchema
  })
};
export {
  ANALYSIS_SAMPLE_SIZES,
  DELETE_CONFIRMATION,
  FOOTBALL_PROP_METRIC_KEYS,
  INGESTION_FAMILIES,
  MLB_PROP_METRIC_KEYS,
  NBA_PROP_METRIC_KEYS,
  NFL_PROP_METRIC_KEYS,
  PLAYER_RADAR_METRIC_KEYS,
  TEAM_MARKET_KEYS,
  acceptedSchema,
  adminIngestionFixtureSchema,
  adminIngestionQuerySchema,
  adminIngestionSchema,
  adminOverviewSchema,
  adminRoutes,
  adminUserDetailsSchema,
  adminUserSchema,
  adminUsersQuerySchema,
  atMostOneScope,
  authRoutes,
  bankrollAdjustmentSchema,
  bankrollCreateSchema,
  bankrollSchema,
  bankrollUpdateSchema,
  betCreateSchema,
  betLegSchema,
  betSchema,
  betUpdateSchema,
  buildOpenApiDocument,
  changePasswordBodySchema,
  closingQuoteSchema,
  competitionDetailSchema,
  competitionFilterSchema,
  competitionQuerySchema,
  competitionReferenceSchema,
  competitionSchema,
  competitionsResponseSchema,
  crossMetricSchema,
  crossMetricsResponseSchema,
  crossMetricsTeamSchema,
  cursorQuerySchema,
  defineRoute,
  deleteAccountBodySchema,
  emailBodySchema,
  emailSchema,
  footballContextSchema,
  formEntrySchema,
  formResultSchema,
  headToHeadMatchSchema,
  healthCheckStateSchema,
  healthRoutes,
  healthStatusSchema,
  idParamsSchema,
  ingestionFamilySchema,
  ingestionFeedSchema,
  ingestionParticipantSchema,
  ingestionStorageSchema,
  ingestionTaskFamilySchema,
  legProgressSchema,
  lineHistorySchema,
  lineQuoteSchema,
  lineSideSchema,
  linesMarketSchema,
  linesResponseSchema,
  lineupStatusSchema,
  localeSchema,
  loginBodySchema,
  loginHistoryEntrySchema,
  loginHistoryQuerySchema,
  marketLineSchema,
  matchAnalysisGameSchema,
  matchAnalysisQuerySchema,
  matchAnalysisRequestSchema,
  matchAnalysisResponseSchema,
  matchAnalysisRoute,
  matchAnalysisTeamSchema,
  matchDetailSchema,
  matchHistoryRadarItemSchema,
  matchLegSchema,
  matchParamsSchema,
  matchParticipantSchema,
  matchPricesSchema,
  matchRefereeSchema,
  matchRoutes,
  matchSchema,
  matchStatisticsPeriodSchema,
  matchStatisticsResponseSchema,
  matchStatisticsRowSchema,
  matchStatisticsTeamSchema,
  matchStatusSchema,
  matchVenueFilterSchema,
  matchesQuerySchema,
  matchesResponseSchema,
  meSchema,
  noContentSchema,
  notebookEntrySchema,
  notebookRoutes,
  notebookSaveSchema,
  oddsFormatSchema,
  openSelectionSchema,
  paginated,
  passwordSchema,
  performanceQuerySchema,
  performanceSchema,
  planStatusSchema,
  playerAnalysisGameSchema,
  playerAnalysisQuerySchema,
  playerAnalysisResponseSchema,
  playerAvailabilityStatusSchema,
  playerDetailSchema,
  playerGameSchema,
  playerGamesQuerySchema,
  playerGamesResponseSchema,
  playerLegSchema,
  playerMetricAverageSchema,
  playerMetricSampleSchema,
  playerMetricsPlayerSchema,
  playerMetricsQuerySchema,
  playerMetricsRequestSchema,
  playerMetricsResponseSchema,
  playerMetricsTeamSchema,
  playerRadarAvailabilitySchema,
  playerRadarItemSchema,
  playerRadarLineupSchema,
  playerRadarOddsSchema,
  playerRadarResponseSchema,
  playerRadarSplitLineSchema,
  playerRadarSplitsSchema,
  playerRadarVersusSchema,
  playerSummarySchema,
  playersQuerySchema,
  playersResponseSchema,
  portfolioRoutes,
  problemFieldErrorSchema,
  problemSchema,
  radarFixtureFields,
  radarHistoryGameSchema,
  radarItemSchema,
  radarQueryBaseSchema,
  radarQuerySchema,
  radarResponseSchema,
  radarRoutes,
  radarSelectionItemSchema,
  radarWarningSchema,
  referenceRoutes,
  registerBodySchema,
  researchSelectionSchema,
  resetPasswordBodySchema,
  rosterPlayerSchema,
  sampleSizeSchema,
  saveTicketSchema,
  savedTicketSchema,
  searchCompetitionSchema,
  searchPlayerSchema,
  searchQuerySchema,
  searchResponseSchema,
  searchTeamSchema,
  seasonPhaseSchema,
  seasonSchema,
  selectionResultSchema,
  settingsSchema,
  sportSchema,
  sportStatusQuerySchema,
  sportStatusResponseSchema,
  standingRowSchema,
  standingsGroupSchema,
  standingsResponseSchema,
  teamAnalysisGameSchema,
  teamAnalysisQuerySchema,
  teamAnalysisResponseSchema,
  teamDetailSchema,
  teamFixturesQuerySchema,
  teamFixturesResponseSchema,
  teamFormSchema,
  teamMetricSampleSchema,
  teamMetricsQuerySchema,
  teamMetricsRequestSchema,
  teamMetricsResponseSchema,
  teamMetricsTeamSchema,
  teamPlayersResponseSchema,
  teamRadarItemSchema,
  teamRadarQuerySchema,
  teamRadarResponseSchema,
  teamRadarRoute,
  teamResearchQuerySchema,
  teamResearchResponseSchema,
  teamSeasonSchema,
  teamStandingSchema,
  teamStandingsResponseSchema,
  teamSummarySchema,
  teamsQuerySchema,
  teamsResponseSchema,
  ticketListQuerySchema,
  ticketMarketSchema,
  ticketOpportunityQuerySchema,
  ticketRoutes,
  ticketSelectionSchema,
  ticketWindowQuerySchema,
  timezoneSchema,
  todayMatchSchema,
  todayQuerySchema,
  todayResponseSchema,
  todayRoutes,
  tokenBodySchema,
  trackedBetSchema,
  trackedLegSchema,
  trackedMatchSchema,
  trackerQuerySchema,
  trackerResponseSchema,
  trackerSummarySchema,
  trackerTabSchema,
  updateAdminUserBodySchema,
  updateSettingsBodySchema,
  updateTicketSchema,
  userRoleSchema,
  userRoutes,
  venueFilterSchema
};
//# sourceMappingURL=index.js.map